import { FunctionComponent } from 'react';

import { useTranslation } from 'react-i18next';

import { msCard, RectangleSkeleton, DateTools } from '@treezor/design-system';

import { useAppSelector } from '@/hooks/redux-helpers';

import { IPaymentCard } from '@/features/cards/store/models/card.model';

import { selectProfileCardByUserIdAndCardId } from '@/store/selectors';

import { usePaymentCardStatus } from '@/features/cards/hooks/usePaymentCardStatus';
import { usePaymentCardType } from '@/features/cards/hooks/usePaymentCardType';

interface IFrontPaymentCardProps {
  paymentCard: IPaymentCard;
}

export const FrontPaymentCard: FunctionComponent<IFrontPaymentCardProps> = ({ paymentCard }) => {
  const { t } = useTranslation();

  const { status, paymentCardColor, paymentCardStatusColor } = usePaymentCardStatus(paymentCard);
  const paymentCardType = usePaymentCardType(paymentCard);

  const isCardLoading = useAppSelector(
    (state) => selectProfileCardByUserIdAndCardId(state, paymentCard.userId, paymentCard.cardId)?.isLoading,
  );

  if (isCardLoading) return <RectangleSkeleton width="100%" height="14rem" margin="0" className="rounded-100" />;

  return (
    <div
      className="relative w-[calc(100%_-_1rem)] my-0 mx-3 h-[14rem] rounded-200 shadow-100"
      style={{ background: paymentCardColor }}
    >
      <div
        className="absolute top-0 left-[10%] w-[80%] text-onColor flex flex-1 flex-col justify-between"
        data-testid={`payment-card-content-${paymentCard.cardId}`}
      >
        <div className="flex flex-col mt-6">
          <div className="flex items-center justify-between">
            <span className="typo-body-regular-paragraph">
              {`${t(`pages.currentProfiles.cards.card.cardPaymentTypes.${paymentCardType}`)} ${t('pages.currentProfiles.cards.card.title')}`}
            </span>
            <label
              className="flex items-center justify-center w-fit rounded-100 font-semibold text-xs py-2 px-4 bg-primary"
              style={{ color: paymentCardStatusColor }}
            >
              {t(`monetics.card.status.${status}`)}
            </label>
          </div>

          <span className="typo-body-regular-caption opacity-70">{paymentCard.embossedName}</span>
        </div>

        <div className="flex flex-col mt-5 gap-4">
          <div className="flex gap-4 typo-body-regular-paragraph">
            <span>{paymentCard?.maskedPan?.substring(0, 4)}</span>
            <span>****</span>
            <span>****</span>
            <span>{paymentCard?.maskedPan?.substring(12, 16)}</span>
          </div>
          <div className="flex justify-between gap-6">
            <div className="flex flex-col gap-3 w-full">
              <div className="flex flex-col">
                <span className="typo-body-regular-caption opacity-70">
                  {t('pages.currentProfiles.cards.card.expireDate')}
                </span>

                <span className="typo-body-regular-small">
                  {DateTools.format(paymentCard.expiryDate, 'expiration')}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex flex-col">
                  <span className="typo-body-regular-caption opacity-70">
                    {t('pages.currentProfiles.cards.card.cardId')}
                  </span>

                  <span className="typo-body-regular-small">{paymentCard.cardId}</span>
                </div>
                <div className="flex flex-col">
                  <span className="typo-body-regular-caption opacity-70">
                    {t('pages.currentProfiles.cards.card.publicToken')}
                  </span>
                  <span className="typo-body-regular-small">{paymentCard.publicToken}</span>
                </div>
              </div>
            </div>
            <div className="self-end">
              <img className="h-8 w-auto" src={msCard} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
